<template>
  <Layout>
    <div class="form-builder">
      <!-- Header -->
      <div class="builder-header">
        <div class="header-left">
          <button class="btn-back" @click="goBack">
            <i class="bx bx-arrow-back"></i>
          </button>
          <div class="form-title-section">
            <input
              v-model="formData.name"
              type="text"
              class="form-title-input"
              placeholder="Nome do formulário"
              @input="markDirty"
            />
            <span
              class="form-status"
              :class="formData.status === 1 ? 'active' : 'inactive'"
            >
              {{ formData.status === 1 ? 'Ativo' : 'Inativo' }}
            </span>
          </div>
        </div>
        <div class="header-right">
          <button class="btn-settings" @click="showSettingsModal = true">
            <i class="bx bx-cog"></i>
            Configurações
          </button>
          <button class="btn-preview" @click="previewForm">
            <i class="bx bx-show"></i>
            Visualizar
          </button>
          <button class="btn-save" @click="saveForm" :disabled="saving">
            <i
              class="bx"
              :class="saving ? 'bx-loader-alt bx-spin' : 'bx-save'"
            ></i>
            {{ saving ? 'Salvando...' : 'Salvar' }}
          </button>
        </div>
      </div>

      <!-- Main Content -->
      <div class="builder-content">
        <!-- Left Panel: Field Palette -->
        <div class="panel panel-left">
          <FieldPalette
            :field-types-grouped="fieldTypesGrouped"
            :loading="loadingTypes"
            @add-field="addCustomField"
            @add-library-field="addLibraryField"
          />
        </div>

        <!-- Center Panel: Form Canvas -->
        <div class="panel panel-center">
          <div class="canvas-header">
            <h5>
              <i class="bx bx-list-ul"></i>
              Campos do Formulário
            </h5>
            <span class="field-count">{{ localFields.length }} campos</span>
          </div>

          <div class="canvas-scroll">
            <FormCanvas
              :fields="localFields"
              :selected-field="selectedField"
              @select-field="handleSelectField"
              @update-field="handleUpdateField"
              @remove-field="handleRemoveField"
              @duplicate-field="handleDuplicateField"
              @reorder-fields="handleReorderFields"
              @add-field="handleCanvasDrop"
            />
          </div>
        </div>

        <!-- Right Panel: Field Editor -->
        <div class="panel panel-right" :class="{ 'has-selection': selectedField }">
          <div v-if="!selectedField" class="no-selection">
            <div class="no-selection-icon">
              <i class="bx bx-pointer"></i>
            </div>
            <h5>Nenhum campo selecionado</h5>
            <p>Clique em um campo para editar suas propriedades</p>
          </div>

          <FieldEditor
            v-else
            :field="selectedField"
            :all-fields="localFields"
            :active-languages="activeLanguages"
            :is-multi-language="isMultiLanguage"
            :default-language-id="defaultLanguageId"
            @update-field="handleUpdateField"
            @close="clearSelection"
          />
        </div>
      </div>

      <!-- Settings Modal -->
      <b-modal
        v-model="showSettingsModal"
        title="Configurações do Formulário"
        size="lg"
        ok-title="Salvar"
        cancel-title="Cancelar"
        @ok="saveSettings"
        centered
      >
        <div class="settings-form">
          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <label>Nome do Formulário *</label>
                <input
                  v-model="formData.name"
                  type="text"
                  class="form-control"
                  placeholder="Digite o nome"
                />
              </div>
            </div>
            <div class="col-md-3">
              <div class="form-group">
                <label>Slug (URL)</label>
                <input
                  v-model="formData.slug"
                  type="text"
                  class="form-control"
                  placeholder="meu-formulario"
                />
              </div>
            </div>
            <div class="col-md-3">
              <div class="form-group">
                <label>Status</label>
                <select v-model.number="formData.status" class="form-control">
                  <option :value="1">Ativo</option>
                  <option :value="0">Inativo</option>
                </select>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label>Descrição</label>
            <textarea
              v-model="formData.description"
              class="form-control"
              rows="2"
              placeholder="Descrição do formulário (opcional)"
            ></textarea>
          </div>

          <hr />

          <h6><i class="mdi mdi-check-circle mr-2"></i>Mensagens de Sucesso</h6>

          <!-- Language Tabs for form settings -->
          <div v-if="isMultiLanguage && activeLanguages.length > 1" class="settings-language-tabs mb-3">
            <div class="d-flex flex-wrap gap-2 mb-2">
              <button
                v-for="lang in activeLanguages"
                :key="lang.id"
                class="btn btn-sm"
                :class="formCurrentLanguageId === lang.id ? 'btn-primary' : 'btn-outline-secondary'"
                @click="selectFormLanguage(lang.id)"
                type="button"
              >
                <span v-if="lang.flag" class="mr-1">{{ lang.flag }}</span>
                {{ lang.name }}
                <span v-if="lang.is_default" class="badge badge-light ml-1">Padrão</span>
              </button>
            </div>
            <small class="text-muted">
              <i class="bx bx-globe mr-1"></i>
              Editando em: <strong class="text-primary">{{ getFormCurrentLanguage() ? getFormCurrentLanguage().native_name : '' }}</strong>
            </small>
          </div>

          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <label>Texto do Botão de Envio</label>
                <input
                  :value="getFormTranslationValue('submit_text')"
                  type="text"
                  class="form-control"
                  :placeholder="isFormDefaultLanguage() ? 'Enviar' : `Tradução para ${getFormCurrentLanguage() ? getFormCurrentLanguage().native_name : ''}`"
                  @input="setFormTranslationValue('submit_text', $event.target.value)"
                />
              </div>
            </div>
          </div>

          <div class="form-group">
            <label>Título da Mensagem de Sucesso</label>
            <input
              :value="getFormTranslationValue('success_title')"
              type="text"
              class="form-control"
              :placeholder="isFormDefaultLanguage() ? 'Inscrição realizada!' : `Tradução para ${getFormCurrentLanguage() ? getFormCurrentLanguage().native_name : ''}`"
              @input="setFormTranslationValue('success_title', $event.target.value)"
            />
          </div>

          <div class="form-group">
            <label>Mensagem de Sucesso</label>
            <textarea
              :value="getFormTranslationValue('success_message')"
              class="form-control"
              rows="3"
              :placeholder="isFormDefaultLanguage() ? 'Sua inscrição foi realizada com sucesso!' : `Tradução para ${getFormCurrentLanguage() ? getFormCurrentLanguage().native_name : ''}`"
              @input="setFormTranslationValue('success_message', $event.target.value)"
            ></textarea>
          </div>
        </div>
      </b-modal>

      <!-- Preview Modal -->
      <b-modal
        v-model="showPreview"
        title="Preview do Formulário"
        size="xl"
        hide-footer
        centered
        body-class="preview-modal-body"
      >
        <div class="form-preview">
          <div class="preview-header">
            <h4>{{ formData.name }}</h4>
            <p v-if="formData.description">{{ formData.description }}</p>
          </div>

          <div class="preview-single-page">
            <div class="preview-fields-grid">
              <div
                v-for="field in localFields"
                :key="field.id || field._tempId"
                class="preview-field"
                :class="getFieldWidthClass(field)"
              >
                <label>
                  {{ field.label }}
                  <span v-if="field.required" class="required-mark">*</span>
                </label>
                <component
                  :is="getPreviewFieldComponent(field)"
                  :field="field"
                />
                <small v-if="field.help_text" class="help-text">{{
                  field.help_text
                }}</small>
              </div>
            </div>

            <div v-if="localFields.length === 0" class="empty-preview">
              <i class="bx bx-layer"></i>
              <p>Nenhum campo adicionado ainda</p>
            </div>

            <div v-else class="preview-submit">
              <button class="btn btn-success" disabled>
                {{ formData.submit_text || 'Enviar' }}
              </button>
            </div>
          </div>
        </div>
      </b-modal>
    </div>
  </Layout>
</template>

<script>
import Layout from '@/components/layout/vertical'
import FieldPalette from './components/FieldPalette.vue'
import FormCanvas from './components/FormCanvas.vue'
import FieldEditor from './components/FieldEditor.vue'
import { mapActions, mapState } from 'vuex'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import translatableMixin from '@/mixins/translatableMixin'

export default {
  name: 'FormBuilder',
  mixins: [translatableMixin],
  components: {
    Layout,
    FieldPalette,
    FormCanvas,
    FieldEditor
  },
  props: {
    id: {
      type: [String, Number],
      default: null
    }
  },
  data () {
    return {
      formData: {
        id: null,
        name: '',
        slug: '',
        description: '',
        status: 1,
        submit_text: 'Enviar',
        success_title: 'Inscrição realizada!',
        success_message: 'Sua inscrição foi realizada com sucesso!',
        settings: {},
        translations: {}
      },
      formCurrentLanguageId: null,
      localFields: [],
      selectedField: null,
      loadingTypes: false,
      saving: false,
      showSettingsModal: false,
      showPreview: false,
      isDirty: false,
      tempIdCounter: 1
    }
  },
  computed: {
    ...mapState('formBuilder', ['fieldTypesGrouped', 'fields']),
    isEditing () {
      return !!this.id
    }
  },
  async mounted () {
    document.body.classList.add('form-builder-page')

    await this.loadFieldTypes()
    await this.loadConditionData()
    await this.loadTranslationSettings()

    // Initialize form language selector with default language
    if (this.defaultLanguageId) {
      this.formCurrentLanguageId = this.defaultLanguageId
    }

    if (this.isEditing) {
      await this.loadForm(this.id)
    }
  },
  beforeDestroy () {
    document.body.classList.remove('form-builder-page')
    this.ClearForm()
  },
  methods: {
    ...mapActions('formBuilder', [
      'GetForm',
      'CreateForm',
      'SaveFormComplete',
      'GetFieldTypesGrouped',
      'GetConditionOperators',
      'GetConditionActions',
      'ClearForm',
      'SetDirty'
    ]),

    generateTempId () {
      return `temp_${Date.now()}_${this.tempIdCounter++}`
    },

    markDirty () {
      this.isDirty = true
      this.SetDirty(true)
    },

    async loadFieldTypes () {
      this.loadingTypes = true
      try {
        await this.GetFieldTypesGrouped()
      } catch (err) {
        console.error('Error loading field types:', err)
      } finally {
        this.loadingTypes = false
      }
    },

    async loadConditionData () {
      try {
        await Promise.all([
          this.GetConditionOperators(),
          this.GetConditionActions()
        ])
      } catch (err) {
        console.error('Error loading condition data:', err)
      }
    },

    async loadForm (id) {
      try {
        await this.GetForm(id)
        const form = this.$store.state.formBuilder.form
        const storeConditions = this.$store.state.formBuilder.conditions || []

        if (form) {
          // Convert form translations from API format (langCode) to internal format (langId)
          let formTranslations = {}
          if (form.translations && this.activeLanguages.length > 0) {
            for (const lang of this.activeLanguages) {
              if (form.translations[lang.code]) {
                formTranslations[lang.id] = form.translations[lang.code]
              }
            }
          }

          this.formData = {
            id: form.id,
            name: form.name || '',
            slug: form.slug || '',
            description: form.description || '',
            status: form.status ? 1 : 0,
            submit_text: form.submit_text || 'Enviar',
            success_title: form.success_title || 'Inscrição realizada!',
            success_message: form.success_message || '',
            settings: form.settings || {},
            translations: formTranslations
          }

          // Map conditions back to their target fields and process translations
          this.localFields = (form.fields || []).map(f => {
            const fieldConditions = storeConditions.filter(
              c => c.target_field_id === f.id
            )

            // Convert translations from API format (langCode) to internal format (langId)
            let translations = {}
            if (f.translations && this.activeLanguages.length > 0) {
              for (const lang of this.activeLanguages) {
                if (f.translations[lang.code]) {
                  translations[lang.id] = f.translations[lang.code]
                }
              }
            }

            return {
              ...f,
              _tempId: f.id ? null : this.generateTempId(),
              conditions: fieldConditions.length > 0 ? fieldConditions : (f.conditions || []),
              translations
            }
          })
        }
      } catch (err) {
        console.error(err)
        errorMessage('Erro ao carregar formulário')
        this.$router.push({ name: 'form-builder' })
      }
    },

    // Field Management
    addLibraryField (libraryField) {
      const newField = {
        ...JSON.parse(JSON.stringify(libraryField)),
        id: null,
        _tempId: this.generateTempId(),
        order: this.localFields.length,
        field_type_id: libraryField.field_type_id || libraryField.fieldType?.id
      }

      if (!newField.name) {
        newField.name = this.generateFieldName(newField.label || 'campo')
      }

      this.localFields.push(newField)
      this.selectedField = newField
      this.markDirty()
    },

    addCustomField (fieldData) {
      const fieldType = fieldData.fieldType || fieldData
      const newField = {
        _tempId: this.generateTempId(),
        label: fieldData.label || fieldType.label || 'Novo Campo',
        name: this.generateFieldName(fieldData.label || fieldType.label || 'campo'),
        fieldType: fieldType,
        field_type_id: fieldData.field_type_id || fieldType.id,
        placeholder: fieldData.placeholder || '',
        help_text: fieldData.help_text || '',
        required: fieldData.required || false,
        order: this.localFields.length,
        options:
          fieldData.options ||
          (['select', 'multiselect', 'radio', 'checkbox'].includes(fieldType.name)
            ? [{ label: 'Opção 1', value: 'option_1' }]
            : null),
        conditions: [],
        validations: [],
        config: {},
        layout: { cols: 12 }
      }
      this.localFields.push(newField)
      this.selectedField = newField
      this.markDirty()
    },

    handleCanvasDrop (dropData) {
      try {
        const parsed =
          typeof dropData === 'string' ? JSON.parse(dropData) : dropData
        if (parsed.source === 'library') {
          this.addLibraryField(parsed.item)
        } else {
          this.addCustomField(parsed.item || parsed)
        }
      } catch (e) {
        this.addCustomField(dropData)
      }
    },

    generateFieldName (label) {
      const base = label
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, '')

      const existing = this.localFields.filter(
        f => f.name && f.name.startsWith(base)
      )
      if (existing.length === 0) {
        return base
      }
      return `${base}_${existing.length + 1}`
    },

    handleSelectField (field) {
      this.selectedField = field
    },

    handleUpdateField (updatedField) {
      const index = this.localFields.findIndex(
        f =>
          (f.id && f.id === updatedField.id) ||
          (f._tempId && f._tempId === updatedField._tempId)
      )
      if (index !== -1) {
        this.$set(this.localFields, index, { ...updatedField })
        this.selectedField = this.localFields[index]
        this.markDirty()
      }
    },

    handleRemoveField (fieldId) {
      const index = this.localFields.findIndex(
        f => f.id === fieldId || f._tempId === fieldId
      )
      if (index !== -1) {
        const field = this.localFields[index]
        const typeName = field.fieldType?.name || field.field_type?.name

        // Prevent removal of protected standard fields
        if (['name', 'email'].includes(typeName)) {
          errorMessage('Não é possível remover campos obrigatórios do sistema (Nome e E-mail)')
          return
        }

        this.localFields.splice(index, 1)
        if (
          this.selectedField &&
          (this.selectedField.id === fieldId ||
            this.selectedField._tempId === fieldId)
        ) {
          this.selectedField = null
        }
        this.markDirty()
      }
    },

    handleDuplicateField (fieldId) {
      const field = this.localFields.find(
        f => f.id === fieldId || f._tempId === fieldId
      )
      if (field) {
        const newField = {
          ...JSON.parse(JSON.stringify(field)),
          id: null,
          _tempId: this.generateTempId(),
          name: this.generateFieldName(field.label + '_copy'),
          label: field.label + ' (cópia)',
          order: this.localFields.length
        }
        this.localFields.push(newField)
        this.selectedField = newField
        this.markDirty()
      }
    },

    handleReorderFields (reorderedFields) {
      this.localFields = reorderedFields.map((field, index) => ({
        ...field,
        order: index
      }))
      this.markDirty()
    },

    clearSelection () {
      this.selectedField = null
    },

    // Form Actions
    async saveForm () {
      if (!this.formData.name) {
        errorMessage('Digite o nome do formulário')
        return
      }

      this.saving = true
      try {
        const fields = this.localFields.map((f, index) => {
          // Process translations - filter out empty values
          let translations = {}
          if (f.translations) {
            for (const [langId, fieldValues] of Object.entries(f.translations)) {
              const nonEmpty = {}
              for (const [field, value] of Object.entries(fieldValues || {})) {
                if (value && (typeof value === 'string' ? value.trim() !== '' : true)) {
                  nonEmpty[field] = value
                }
              }
              if (Object.keys(nonEmpty).length > 0) {
                translations[langId] = nonEmpty
              }
            }
          }

          return {
            ...f,
            order: index,
            id: f.id || undefined,
            field_type_id: f.field_type_id || f.fieldType?.id,
            translations: Object.keys(translations).length > 0 ? translations : undefined
          }
        })

        // Extract conditions from fields and format them for the API
        const conditions = this.extractConditionsFromFields(fields)

        // Process form translations
        let formTranslations = {}
        if (this.formData.translations) {
          for (const [langId, fieldValues] of Object.entries(this.formData.translations)) {
            const nonEmpty = {}
            for (const [field, value] of Object.entries(fieldValues || {})) {
              if (value && value.trim() !== '') {
                nonEmpty[field] = value
              }
            }
            if (Object.keys(nonEmpty).length > 0) {
              formTranslations[langId] = nonEmpty
            }
          }
        }

        if (!this.isEditing) {
          const payload = {
            ...this.formData,
            translations: Object.keys(formTranslations).length > 0 ? formTranslations : undefined,
            fields,
            conditions
          }
          const form = await this.CreateForm(payload)
          this.$router.replace({
            name: 'form-builder-edit',
            params: { id: form.id }
          })
          successMessage('Formulário criado com sucesso!')
        } else {
          const formDataWithTranslations = {
            ...this.formData,
            translations: Object.keys(formTranslations).length > 0 ? formTranslations : undefined
          }
          this.$store.commit('formBuilder/SET_FORM', formDataWithTranslations)
          this.$store.commit('formBuilder/SET_FIELDS', fields)
          this.$store.commit('formBuilder/SET_CONDITIONS', conditions)
          await this.SaveFormComplete()
          await this.loadForm(this.formData.id)
          successMessage('Formulário salvo com sucesso!')
        }

        this.isDirty = false
        this.SetDirty(false)
      } catch (err) {
        console.error(err)
        errorMessage(
          'Erro ao salvar: ' + (err.response?.data?.message || err.message)
        )
      } finally {
        this.saving = false
      }
    },

    extractConditionsFromFields (fields) {
      const conditions = []
      fields.forEach(field => {
        if (field.conditions && field.conditions.length > 0) {
          field.conditions.forEach(condition => {
            conditions.push({
              ...condition,
              target_field_id: field.id || field._tempId,
              form_id: this.formData.id
            })
          })
        }
      })
      return conditions
    },

    saveSettings () {
      this.formData.status = this.formData.status ? 1 : 0
      this.showSettingsModal = false
      this.markDirty()

      if (this.isEditing) {
        this.saveForm()
      }
    },

    previewForm () {
      this.showPreview = true
    },

    goBack () {
      if (this.isDirty) {
        this.$bvModal
          .msgBoxConfirm('Existem alterações não salvas. Deseja sair mesmo assim?', {
            title: 'Atenção',
            okVariant: 'danger',
            okTitle: 'Sair sem salvar',
            cancelTitle: 'Continuar editando',
            centered: true
          })
          .then(confirmed => {
            if (confirmed) {
              this.$router.push({ name: 'form-builder' })
            }
          })
      } else {
        this.$router.push({ name: 'form-builder' })
      }
    },

    // Form translation helpers
    selectFormLanguage (langId) {
      this.formCurrentLanguageId = langId
    },

    isFormDefaultLanguage () {
      return this.formCurrentLanguageId === this.defaultLanguageId
    },

    getFormCurrentLanguage () {
      if (!this.formCurrentLanguageId) return null
      return this.activeLanguages.find(l => l.id === this.formCurrentLanguageId)
    },

    getFormTranslationValue (field) {
      if (this.isFormDefaultLanguage()) {
        return this.formData[field] || ''
      }
      if (this.formData.translations &&
          this.formData.translations[this.formCurrentLanguageId] &&
          this.formData.translations[this.formCurrentLanguageId][field] !== undefined) {
        return this.formData.translations[this.formCurrentLanguageId][field]
      }
      return ''
    },

    setFormTranslationValue (field, value) {
      if (this.isFormDefaultLanguage()) {
        this.formData[field] = value
      } else {
        if (!this.formData.translations) {
          this.formData.translations = {}
        }
        if (!this.formData.translations[this.formCurrentLanguageId]) {
          this.formData.translations[this.formCurrentLanguageId] = {}
        }
        this.formData.translations[this.formCurrentLanguageId][field] = value
      }
      this.markDirty()
    },

    // Preview helpers
    getFieldWidthClass (field) {
      const cols = field.layout?.cols || '12'
      return {
        'col-12': cols === '12' || cols === 12,
        'col-6': cols === '6' || cols === 6,
        'col-4': cols === '4' || cols === 4,
        'col-3': cols === '3' || cols === 3
      }
    },

    getPreviewFieldComponent (field) {
      const typeName = field.fieldType?.name || field.field_type?.name || 'text'
      const textTypes = [
        'text',
        'email',
        'phone',
        'cpf',
        'cnpj',
        'cep',
        'number',
        'password',
        'rg'
      ]
      const selectTypes = ['select', 'multiselect', 'state']

      if (typeName === 'textarea') {
        return {
          functional: true,
          props: ['field'],
          render: (h, ctx) =>
            h('textarea', {
              class: 'form-control',
              attrs: {
                placeholder: ctx.props.field.placeholder,
                rows: 3,
                disabled: true
              }
            })
        }
      }

      if (selectTypes.includes(typeName)) {
        return {
          functional: true,
          props: ['field'],
          render: (h, ctx) =>
            h(
              'select',
              { class: 'form-control', attrs: { disabled: true } },
              [
                h('option', ctx.props.field.placeholder || 'Selecione...'),
                ...(ctx.props.field.options || []).map(opt =>
                  h('option', { attrs: { value: opt.value } }, opt.label)
                )
              ]
            )
        }
      }

      if (typeName === 'radio' || typeName === 'checkbox') {
        return {
          functional: true,
          props: ['field'],
          render: (h, ctx) =>
            h(
              'div',
              { class: 'options-preview' },
              (ctx.props.field.options || []).map(opt =>
                h('div', { class: 'form-check' }, [
                  h('input', {
                    class: 'form-check-input',
                    attrs: { type: typeName, disabled: true }
                  }),
                  h('label', { class: 'form-check-label' }, opt.label)
                ])
              )
            )
        }
      }

      if (textTypes.includes(typeName)) {
        return {
          functional: true,
          props: ['field'],
          render: (h, ctx) =>
            h('input', {
              class: 'form-control',
              attrs: {
                type: 'text',
                placeholder: ctx.props.field.placeholder,
                disabled: true
              }
            })
        }
      }

      // Fallback
      return {
        functional: true,
        props: ['field'],
        render: (h, ctx) =>
          h('input', {
            class: 'form-control',
            attrs: {
              type: 'text',
              placeholder: ctx.props.field.placeholder,
              disabled: true
            }
          })
      }
    }
  }
}
</script>

<style scoped>
.form-builder {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f5f6fa;
  overflow: hidden;
}

/* Header */
.builder-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: #fff;
  border-bottom: 1px solid #e9ecef;
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.btn-back {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e9ecef;
  background: #fff;
  border-radius: 8px;
  cursor: pointer;
  color: #495057;
  transition: all 0.15s;
}

.btn-back:hover {
  border-color: #556ee6;
  color: #556ee6;
}

.form-title-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.form-title-input {
  border: none;
  background: transparent;
  font-size: 18px;
  font-weight: 600;
  color: #344054;
  padding: 4px 0;
  width: 300px;
}

.form-title-input:focus {
  outline: none;
  border-bottom: 2px solid #556ee6;
}

.form-status {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
}

.form-status.active {
  background: #d1fae5;
  color: #059669;
}

.form-status.inactive {
  background: #fee2e2;
  color: #dc2626;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-settings,
.btn-preview {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid #e9ecef;
  background: #fff;
  border-radius: 6px;
  font-size: 13px;
  color: #495057;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-settings:hover,
.btn-preview:hover {
  border-color: #556ee6;
  color: #556ee6;
}

.btn-save {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 20px;
  border: none;
  background: #556ee6;
  color: #fff;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-save:hover:not(:disabled) {
  background: #485ec4;
}

.btn-save:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Main Content */
.builder-content {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.panel {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panel-left {
  width: 320px;
  border-right: 1px solid #e9ecef;
  background: #fff;
  flex-shrink: 0;
}

.panel-center {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: #f5f6fa;
}

.canvas-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: #fff;
  border-bottom: 1px solid #e9ecef;
}

.canvas-header h5 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #495057;
  display: flex;
  align-items: center;
  gap: 8px;
}

.canvas-header h5 i {
  color: #556ee6;
}

.canvas-header .field-count {
  font-size: 12px;
  color: #6c757d;
}

.canvas-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  min-height: 0;
  scrollbar-width: thin;
  scrollbar-color: #d0d5dd transparent;
}

.panel-right {
  width: 0;
  background: #fff;
  border-left: 1px solid #e9ecef;
  transition: width 0.2s ease;
  overflow: hidden;
  flex-shrink: 0;
}

.panel-right.has-selection {
  width: 360px;
}

.no-selection {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 32px;
  text-align: center;
}

.no-selection-icon {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f4ff;
  border-radius: 50%;
  margin-bottom: 16px;
}

.no-selection-icon i {
  font-size: 28px;
  color: #556ee6;
}

.no-selection h5 {
  font-size: 15px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
}

.no-selection p {
  font-size: 13px;
  color: #6c757d;
  margin: 0;
}

/* Settings Form */
.settings-form .form-group {
  margin-bottom: 16px;
}

.settings-form label {
  font-size: 13px;
  font-weight: 500;
  color: #344054;
  margin-bottom: 6px;
  display: block;
}

.settings-form h6 {
  font-size: 14px;
  font-weight: 600;
  color: #344054;
  margin-bottom: 16px;
}

.settings-language-tabs {
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
}

.settings-language-tabs .gap-2 {
  gap: 8px;
}

/* Preview */
.form-preview {
  padding: 0;
}

.preview-header {
  text-align: center;
  padding: 24px 24px 16px;
  border-bottom: 1px solid #e9ecef;
}

.preview-header h4 {
  margin: 0 0 8px 0;
  font-size: 20px;
  font-weight: 600;
  color: #344054;
}

.preview-header p {
  margin: 0;
  font-size: 14px;
  color: #6c757d;
}

.preview-single-page {
  padding: 24px;
}

.preview-fields-grid {
  display: flex;
  flex-wrap: wrap;
  margin: 0 -12px;
}

.preview-field {
  padding: 0 12px;
  margin-bottom: 20px;
}

.preview-field.col-12 {
  width: 100%;
}
.preview-field.col-6 {
  width: 50%;
}
.preview-field.col-4 {
  width: 33.333%;
}
.preview-field.col-3 {
  width: 25%;
}

.preview-field label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #344054;
  margin-bottom: 6px;
}

.preview-field .required-mark {
  color: #dc2626;
}

.preview-field .help-text {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #9ca3af;
}

.preview-submit {
  display: flex;
  justify-content: center;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e9ecef;
}

.empty-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  color: #9ca3af;
}

.empty-preview i {
  font-size: 48px;
  margin-bottom: 16px;
}

.empty-preview p {
  margin: 0;
  font-size: 14px;
}

/* Responsive */
@media (max-width: 1400px) {
  .panel-left {
    width: 280px;
  }
  .panel-right.has-selection {
    width: 320px;
  }
}

@media (max-width: 1200px) {
  .panel-right.has-selection {
    width: 300px;
  }
}
</style>

<style>
/* Global style to hide footer on form builder page */
body.form-builder-page {
  overflow: hidden !important;
  height: 100vh !important;
}

body.form-builder-page footer.footer {
  display: none !important;
}

body.form-builder-page .main-content {
  padding: 0 !important;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  margin-right: 0 !important;
  overflow: hidden !important;
  height: 100vh !important;
  display: flex !important;
  flex-direction: column !important;
}

body.form-builder-page .page-content {
  padding: 0 !important;
  margin: 0 !important;
  margin-top: 70px !important;
  overflow: hidden !important;
  min-height: 0 !important;
  height: calc(100vh - 70px) !important;
  max-height: calc(100vh - 70px) !important;
  flex: 1 !important;
  display: flex !important;
  flex-direction: column !important;
}

body.form-builder-page .page-content > .container-fluid {
  padding: 0 !important;
  margin: 0 !important;
  height: 100% !important;
  min-height: 0 !important;
  max-width: 100% !important;
  flex: 1 !important;
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
}
</style>
